import { DialogPositioningBehavior, DialogType, DialogWidget, OriginOptions } from '@tableau/widgets';
import React from 'react';

export type ButtonWithUberDialogProps = {

  /** Render prop for the button that toggles the dialog. It should call `showDialog` when it's "clicked". */
  readonly renderButton: (showDialog: () => void) => React.ReactElement;

  /** The content of the uber dialog. */
  readonly uberDialogContent: React.ReactElement;

  /**
   * This property must be set to true if applicable, otherwise the dialog will crash while trying
   * to focus on its content
   */
  readonly dialogHasNoFocusableElements?: boolean;

  /** Same as anchorOrigin in DialogWidget props */
  readonly anchorOrigin?: OriginOptions;
  /** Same as stackedOrigin in DialogWidget props */
  readonly stackedOrigin?: OriginOptions;

  /** Specify the dialog width */
  readonly dialogWidth?: number;

  /** function to run on dialog close  */
  readonly onDialogClose?: () => void;
};

/**
 * A common use case of the Uber Dialog, where a button is clicked to show a dialog adjacent to that button.
 * The dialog will close when anything is clicked outside of it, or when the escape key is pressed.
 */
export function ButtonWithUberDialog(props: ButtonWithUberDialogProps): JSX.Element {

  const anchorRef = React.useRef<HTMLElement>(null);

  const [uberDialogVisible, setUberDialogVisible] = React.useState(false);

  const hideUberDialog = React.useCallback(
    () => {
      if (typeof props.onDialogClose === 'function') {
        props.onDialogClose();
      }
      setUberDialogVisible(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setUberDialogVisible, props.onDialogClose]
  );
  const showUberDialog = React.useCallback(
    () => setUberDialogVisible(true),
    [setUberDialogVisible]
  );

  return (
    <>
      <span ref={anchorRef}>
        { props.renderButton(showUberDialog) }
      </span>
      { uberDialogVisible &&
        <UberDialog
          closeDialog={hideUberDialog}
          anchorSpan={anchorRef.current}
          content={props.uberDialogContent}
          dialogWidth={props.dialogWidth}
          disableAutoFocus={!!props.dialogHasNoFocusableElements}
          anchorOrigin={props.anchorOrigin}
          stackedOrigin={props.stackedOrigin}
        />
      }
    </>
  );
}


type UberDialogProps = {
  readonly closeDialog: () => void;
  readonly anchorSpan: HTMLElement;
  readonly content: React.ReactElement;
  readonly dialogWidth: number;
  readonly disableAutoFocus: boolean;
  readonly anchorOrigin?: OriginOptions;
  readonly stackedOrigin?: OriginOptions;
};

function UberDialog(props: UberDialogProps): JSX.Element {

  const getAnchorRect = React.useCallback(
    () => (props.anchorSpan.firstElementChild as HTMLElement).getBoundingClientRect(),
    [props.anchorSpan]
  );

  return (
    <DialogWidget
      type={DialogType.Uber}
      isModal={true}
      isGlassTransparent={true}
      handleGlassClick={props.closeDialog}
      onCloseRequest={props.closeDialog}
      shouldReturnFocusOnClose={true}
      testId="uber-dialog-from-toggle-button"
      showCloseButton={false}
      showFooter={false}
      disableAutoFocus={props.disableAutoFocus}
      positioningBehavior={DialogPositioningBehavior.Anchored}
      getAnchorClientRect={getAnchorRect}
      anchorOrigin={props.anchorOrigin}
      stackedOrigin={props.stackedOrigin}
      dialogWidth={props.dialogWidth || undefined}
      title={''} // title bar is not shown for an Uber dialog type
    >
      {props.content}
    </DialogWidget>
  );
}